import {
  PermissionCode,
  TICKET_STATUS_LABELS,
  type TicketAiAssistResponse,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { CopyIcon, Loader2Icon, MessageSquareIcon, SparklesIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { AiLiveReply } from '@/platform/components/ai-live-reply';
import { AiMarkdown } from '@/platform/components/ai-markdown';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import {
  commentBodyFromAssist,
  type TicketCommentDraft,
} from '@/products/helpdesk/features/tickets/ticket-ai-assist-comment';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import {
  useLatestTicketAiAssist,
  useRequestTicketAiAssist,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';

export type { TicketCommentDraft };

export function TicketAiAssistPanel({
  ticketRef,
  onApplyToEditor,
  canComment = true,
  canNoteInternal = false,
}: {
  ticketRef: string;
  onApplyToEditor: (draft: TicketCommentDraft) => void;
  canComment?: boolean;
  canNoteInternal?: boolean;
}) {
  const me = useMe();
  const paths = useAppPaths();
  const canUse = hasPermission(me.data, PermissionCode.TICKETS_READ_ALL);
  const latest = useLatestTicketAiAssist(ticketRef, { enabled: canUse });
  const request = useRequestTicketAiAssist(ticketRef);
  const [question, setQuestion] = useState('');

  if (!canUse || paths.isPortal) {
    return null;
  }

  const run: TicketAiAssistResponse | null = request.data ?? latest.data ?? null;
  const busy = request.isPending || latest.isFetching;

  async function onAsk() {
    try {
      await request.mutateAsync({ question: question.trim() || undefined });
      toast.success('KI-Empfehlung liegt vor. Bitte prüfen — die KI kann irren.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die KI-Empfehlung konnte nicht eingeholt werden.',
      );
    }
  }

  async function copyText(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('In die Zwischenablage kopiert.');
    } catch {
      toast.error('Kopieren nicht möglich.');
    }
  }

  return (
    <TicketSectionCard
      tone="navy"
      icon={SparklesIcon}
      collapsible
      defaultOpen={false}
      title="KI-Ticket-Hilfe"
      description="Liest Anfrage, Antworten und spätere Klarstellungen."
    >
      <div className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Vorschlag prüfen — die KI kann irren. Es wird nichts automatisch an den Anfragenden
          gesendet.
        </p>
        <div className="space-y-2">
          <Label htmlFor="ai-assist-question">Eigene Frage an die KI (optional)</Label>
          <textarea
            id="ai-assist-question"
            rows={3}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            maxLength={2000}
            className="min-h-[4.5rem] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            placeholder="z. B. Woran erkenne ich, dass der Drucker offline ist?"
          />
        </div>
        <Button type="button" size="sm" disabled={busy} onClick={() => void onAsk()}>
          {request.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Empfehlung einholen
        </Button>
        {request.isPending ? <AiLiveReply active text="" /> : null}
        {latest.isPending && !run && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Letzte Empfehlung wird geladen …
          </p>
        )}
        {run && (
          <AssistResult
            run={run}
            canComment={canComment}
            canNoteInternal={canNoteInternal}
            onCopy={copyText}
            onApply={onApplyToEditor}
          />
        )}
      </div>
    </TicketSectionCard>
  );
}

function AssistResult({
  run,
  canComment,
  canNoteInternal,
  onCopy,
  onApply,
}: {
  run: TicketAiAssistResponse;
  canComment: boolean;
  canNoteInternal: boolean;
  onCopy: (value: string) => Promise<void>;
  onApply: (draft: TicketCommentDraft) => void;
}) {
  const copy = useVoiceCopy();
  const paths = useAppPaths();
  const publicBody = commentBodyFromAssist(run, 'public');
  const internalBody = commentBodyFromAssist(run, 'internal');

  return (
    <div className="space-y-4 border-t border-border pt-4">
      {!run.chatAvailable && (
        <p className="rounded-md border border-status-warning-border bg-status-warning px-3 py-2 text-status-warning-foreground">
          {copy('helpdesk.ai.assist.offline')}
        </p>
      )}
      {canComment && publicBody ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => onApply({ body: publicBody, isInternal: false })}
            >
              <MessageSquareIcon aria-hidden />
              In Kommentar übernehmen
            </Button>
            {canNoteInternal && internalBody ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onApply({ body: internalBody, isInternal: true })}
              >
                Als interne Notiz übernehmen
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">{copy('helpdesk.ai.assist.send-later')}</p>
        </div>
      ) : null}
      <div>
        <h3 className="mb-1 font-medium text-primary">
          {run.question ? 'Antwort auf die Frage' : 'Kurzfassung'}
        </h3>
        {run.question ? <p className="mb-2 text-muted-foreground">{run.question}</p> : null}
        <AiMarkdown text={run.summary} />
      </div>
      {run.steps.length > 0 && (
        <div>
          <h3 className="mb-1 font-medium text-primary">Empfohlene Schritte</h3>
          <ol className="list-decimal space-y-1 pl-5">
            {run.steps.map((step, index) => (
              <li key={`${index}-${step.slice(0, 24)}`}>
                <AiMarkdown text={step} />
              </li>
            ))}
          </ol>
        </div>
      )}
      {run.followUpQuestions.length > 0 && (
        <div>
          <h3 className="mb-1 font-medium text-primary">Rückfragen an den Mitarbeiter</h3>
          <ul className="list-disc space-y-1 pl-5">
            {run.followUpQuestions.map((item) => (
              <li key={item}>
                <AiMarkdown text={item} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {run.sources.length > 0 && (
        <div>
          <h3 className="mb-1 font-medium text-primary">Wissensartikel</h3>
          <ul className="space-y-1">
            {run.sources.map((source) => (
              <li key={source.slug}>
                <Link
                  className="font-medium text-primary underline"
                  to={paths.article(source.slug)}
                >
                  {source.title}
                </Link>
                {source.summary ? (
                  <span className="text-muted-foreground"> — {source.summary}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {run.similarTickets.length > 0 && (
        <div>
          <h3 className="mb-1 font-medium text-primary">Ähnliche Tickets</h3>
          <ul className="space-y-1">
            {run.similarTickets.map((item) => (
              <li key={item.number}>
                <Link className="font-medium text-primary underline" to={paths.ticket(item.number)}>
                  #{item.number} {item.subject}
                </Link>
                <span className="text-muted-foreground">
                  {' '}
                  · {TICKET_STATUS_LABELS[item.status]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {run.publicReply && (
        <DraftBlock
          title="Entwurf öffentliche Antwort"
          body={run.publicReply}
          applyLabel="In Kommentar übernehmen"
          onCopy={() => void onCopy(run.publicReply ?? '')}
          onApply={
            canComment
              ? () => onApply({ body: run.publicReply ?? '', isInternal: false })
              : undefined
          }
        />
      )}
      {run.internalNote && (
        <DraftBlock
          title="Entwurf interne Notiz"
          body={run.internalNote}
          applyLabel="Als interne Notiz übernehmen"
          onCopy={() => void onCopy(run.internalNote ?? '')}
          onApply={
            canComment && canNoteInternal
              ? () => onApply({ body: run.internalNote ?? '', isInternal: true })
              : undefined
          }
        />
      )}
    </div>
  );
}

function DraftBlock({
  title,
  body,
  applyLabel,
  onCopy,
  onApply,
}: {
  title: string;
  body: string;
  applyLabel: string;
  onCopy: () => void;
  onApply?: () => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-card px-3 py-3">
      <h3 className="font-medium text-primary">{title}</h3>
      <AiMarkdown text={body} />
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCopy}>
          <CopyIcon aria-hidden />
          Kopieren
        </Button>
        {onApply ? (
          <Button type="button" size="sm" variant="outline" onClick={onApply}>
            {applyLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
